<template>
  <div class="human">
    <div class="btn-group btn-group-horizontal">
      <button type="button"
              role="button"
              class="btn btn-sm"
              :class="{'btn-active': position === 'front'}"
              @click.prevent="togglePosition('front')"
      >
        Front
      </button>

      <button type="button"
              role="button"
              class="btn btn-sm"
              :class="{'btn-active': position === 'back'}"
              @click.prevent="togglePosition('back')"
      >
        Back
      </button>
    </div>

    <div class="human-wrap mt-2" :class="`human-${position}`">
      <img src="../../assets/img/model-front.svg" alt="Human model (Front)" class="human-img-front">
      <img src="../../assets/img/model-back.svg" alt="Human model (Back)" class="human-img-back">
      <div class="human-parts" ref="parts"></div>
    </div>
  </div>
</template>

<script>
export default {
  name: "Human",

  props: {
    value: String,
  },

  data() {
    return {
      position: "front",

      bodyParts: {
        front: {
          shoulderFrontLeft: { x: 171, y: 145 },
          shoulderFrontRight: { x: 65, y: 145 },
          upperArmRight: { x: 48, y: 200 },
          upperArmLeft: { x: 188, y: 200 },
          forearmRight: { x: 34, y: 265 },
          forearmLeft: { x: 203, y: 265 },
          handAndWristRight: { x: 20, y: 307 },
          handAndWristLeft: { x: 216, y: 307 },
          groinLeft: { x: 146, y: 325 },
          groinRight: { x: 90, y: 325 },
          thighFrontLeft: { x: 151, y: 375 },
          thighFrontRight: { x: 85, y: 375 },
          kneeRight: { x: 80, y: 450 },
          kneeLeft: { x: 156 , y: 450 },
          shinRight: { x: 80, y: 515 },
          shinLeft: { x: 156 , y: 515 },
          footAndAnkleRight: { x: 78, y: 575 },
          footAndAnkleLeft: { x: 158, y: 575 },
        },

        back: {
          neck: { x: 118, y: 95 },
          shoulderBackLeft: { x: 65, y: 145 },
          shoulderBackRight: { x: 171, y: 145 },
          elbowLeft: { x: 42, y: 235 },
          elbowRight: { x: 195, y: 235 },
          thoracic: { x: 118, y: 190 },
          lumbar: { x: 118, y: 265 },
          hipLeft: { x: 62, y: 310 },
          hipRight: { x: 175, y: 310 },
          thighBackLeft: { x: 85, y: 375 },
          thighBackRight: { x: 151, y: 375 },
          posteriorKneeLeft: { x: 84, y: 445 },
          posteriorKneeRight: { x: 154, y: 445 },
          calfLeft: { x: 82, y: 500 },
          calfRight: { x: 156, y: 500 },
          heelLeft: { x: 82, y: 593 },
          heelRight: { x: 156, y: 593 },
        },
      },

      elements: [],
    };
  },

  watch: {
    value(part) {
      this.clearHighlightedBodyPart();

      if (part) {
        const position = this.getBodyPartPosition(part);

        if (position !== this.position) {
          this.togglePosition();
        }

        for (let i in this.elements) {
          if (this.elements.hasOwnProperty(i)) {
            if (this.elements[i].getAttribute("data-part") === part) {
              this.elements[i].classList.add("active");
              break;
            }
          }
        }
      }
    },
  },

  mounted() {
    this.addBodyParts();
  },

  methods: {
    togglePosition(position) {
      if (!position) {
        position = this.position === 'front' ? 'back' : 'front';
      }

      if (this.position === position) {
        return;
      }

      let element;

      while (this.elements.length > 0) {
        element = this.elements.pop();

        this.$refs.parts.removeChild(element);
      }

      this.position = position;

      this.addBodyParts();
    },

    getBodyPartPosition(part) {
      for (let i in this.bodyParts.front) {
        if (this.bodyParts.front.hasOwnProperty(i)) {
          if (i === part) {
            return "front";
          }
        }
      }

      return "back";
    },

    addBodyParts() {
      let part, x, y, active;

      for (part in this.bodyParts[this.position]) {
        if (this.bodyParts[this.position].hasOwnProperty(part)) {
          x = this.bodyParts[this.position][part].x;
          y = this.bodyParts[this.position][part].y;
          active = this.value === part;

          this.addPoint(part, x, y, active);
        }
      }
    },

    addPoint(name, x, y, active) {
      const svg = this.makeSvgElement(name, active);

      svg.style.top = `${y}px`;
      svg.style.left = `${x}px`;

      this.$refs.parts.appendChild(svg);
    },

    makeSvgElement(name, active) {
      const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");

      svg.setAttribute("width", "24");
      svg.setAttribute("height", "24");
      svg.setAttribute("data-part", name);

      if (active) {
        svg.classList.add("active");
      }

      const cir = document.createElementNS("http://www.w3.org/2000/svg", "circle");

      cir.setAttribute("cx", "12");
      cir.setAttribute("cy", "12");
      cir.setAttribute("r", "12");
      cir.setAttribute("fill", "#fff");
      cir.setAttribute("data-part", name);
      cir.onclick = this.selectBodyPart;

      svg.appendChild(cir);

      this.elements.push(svg);

      return svg;
    },

    clearHighlightedBodyPart() {
      for (let i in this.elements) {
        if (this.elements.hasOwnProperty(i)) {
          this.elements[i].classList.remove("active");
        }
      }
    },

    selectBodyPart(e) {
      const part = e.target.getAttribute("data-part");

      this.clearHighlightedBodyPart();

      e.target.parentNode.classList.add("active");

      this.$emit("change", part);
    },
  },
}
</script>

<style lang="scss">
.human {
  position: relative;
  width: 260px;
  margin: 2rem auto;
  overflow: hidden;
  text-align: center;

  @include screen(md) {
    margin-top: 0;
    margin-bottom: 0;
  }

  img {
    display: block;
    position: relative;
    width: 100%;
    height: auto;
  }

  .human-img-back {
    display: none;
  }

  .link {
    margin-top: 2rem;
  }
}

.human-wrap {
  position: relative;
  width: 100%;

  &.human-back {
    .human-img-front {
      display: none;
    }

    .human-img-back {
      display: block;
    }
  }
}

.human-parts {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;

  svg {
    position: absolute;

    circle {
      @include hover {
        fill: $color-casper;
        cursor: pointer;
      }
    }

    &.active {
      circle {
        fill: $color-pizazz;

        @include hover {
          fill: $color-pizazz;
        }
      }
    }
  }
}
</style>
